import { isString } from '../utils/types';

export interface Program {
  id: number;
  year: number;
  title: string
  title_en: string;
  theme: string;
  theme_en: string;
  location: string;
  location_en: string;
  address: string;
  address_en: string;
  main_video_link?: string;
  main_video_preview?: string;
  stream_link?: string;

  dates?: { date: StringOrDate, sections: ProgramSection[] }[],
}

export type StringOrDate = string | Date;


export interface ProgramSection {
  id: number;
  name: string;
  date: StringOrDate;
  start_at: string;
  finish_at: string;
  location: string;
  is_coffee_break?: boolean;
  subject: string;
  description?: string;
  speakers?: Speaker[];
  moderators?: Speaker[];
  speeches?: ProgramSpeech[];
  files?: FileAttachment[];
}

export interface ProgramSpeech {
  id: number;
  date: StringOrDate;
  start_at: string;
  finish_at: string;
  is_coffee_break?: boolean;
  subject?: string;
  description?: string;
  speakers?: Speaker[];
  moderators?: Moderator[];
}

export interface FileAttachment {
  title: string;
  url: string;
}

export interface Speaker {
  id: number;
  full_name: string;
  image: string;
  avatar?: string;
  organization?: string;
  location?: string;
  description?: string;
  tags?: Tag[];
  link?: string;
}

export interface Moderator {
  id: number;
  full_name: string;
  organization?: string;
}

export interface Partner {
  id: number;
  logo: string;
  link?: string;
}

export interface News {
  id: number;
  title: string;
  is_main: boolean;
  published_at: StringOrDate;
  preview_image?: NewsImage;
  head_image: NewsImage;
  has_video?: boolean;

  lead?: string;
  blocks?: (NewsBlockType)[];
  read_also?: News[];

  // source?: string;
  // source_link?: string;
  // author?: string;
  // author_link?: string;
  // tags?: Tag[];
}

export type NewsBlockType = TextBlockType | ImageBlockType | QuoteBlockType | VideoBlockType | DividerBlockType;

export interface NewsImage {
  image: string;
  small?: string;
  caption?: string;
  credit?: string;
}

export interface TextBlockType {
  id: number;
  kind: 'text';
  text?: string;
}

export interface ImageBlockType {
  id: number;
  kind: 'image';
  images?: NewsImage[];
}

export interface QuoteBlockType {
  id: number;
  kind: 'quote';
  text?: string;
  caption?: string;
}

export interface VideoBlockType {
  id: number;
  kind: 'video';
  video_url?: string;
  image?: NewsImage;
}

export interface DividerBlockType {
  id: number;
  kind: 'divider';
}

export interface SizedImage {
  src: string;
  image?: string;
  thumbnail?: string;
}

export type ResponsiveImage = string | SizedImage;

export function getImage(image: ResponsiveImage, sizedField: keyof SizedImage = 'src'): string {
  if (isString(image)) return image.toString();
  const sized = image as SizedImage;
  return sized[sizedField] || sized.src;
}

export interface GalleryImage {
  id: number;
  image: ResponsiveImage;
  preview?: ResponsiveImage;
}

export interface About {
  description: string;
  description_en: string;
  video_link?: string;
  video_preview?: string;
  video_title?: string;
  video_title_en?: string;
  people_count?: number;
  city_count?: number;
  country_count?: number;
}

export interface ForumData {
  program: Program;
  about: About;
  speakers: Speaker[];
  partners: Partner[];
  gallery_images?: GalleryImage[]
}

export interface ArchiveForumData {
  description?: string;
  results: ArchiveForum[];
}

export interface ArchiveForum {
  year: number;
  title: string;
  description: string;
  dates: { date: StringOrDate }[];
  program?: { description: string; };
  speakers?: { description: string; };
  videos?: { link: string; description: string; };
}

export interface SignUpData {
  steps: SignUpStep[];
  with_accommodation_role1?: string;
  without_accommodation_role1?: string;
  with_accommodation_role2?: string;
  without_accommodation_role2?: string;
}

export interface SignUpStep {
  alias: 'terms' | 'accommodation' | 'role' | string;
  title: string;
  content: { title?: string, content?: string };
}

export interface Tag {
  name: string;
  link?: string;
}

export interface Filters {
  years?: YearTag[];
}

export interface YearTag {
  year: number;
}

export type SearchSuggestion = string;

export interface SearchResult {
  type: 'speaker' | 'program' | 'section' | 'news';
  object: Speaker | Program | ProgramSection | News | null;
}

export interface DynamicPage {
  title: string;
  content: string;
}

export interface Pagination<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}
